// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import Vue from 'vue';
import { LogFactory } from '../helpers/debug';

const log = LogFactory('IntercomPlugin', false);
const INTERCOM_ID = process.env.VUE_APP_INTERCOM_ID || '';
const INTERCOM_API_BASE = process.env.VUE_APP_INTERCOM_API_BASE || 'https://api-iam.intercom.io';
const SCRIPT_LOAD_TIMEOUT_MS = 10000;
const SCRIPT_POLL_INTERVAL_MS = 100;

// Single source of truth for the support fallback used by every "Get Help" /
// "Contact us" entry point. Centralized here (alongside openOrFallback) so the
// fallback URL and the booted-vs-fallback decision cannot drift between callers.
export const SUPPORT_FALLBACK_URL =
  'https://jira.linuxfoundation.org/plugins/servlet/theme/portal/4/create/384';

let instance;

export const getInstance = () => instance;

export const useIntercom = () => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        isLoaded: false,
        isBooted: false,
        isLoading: false,
        bootedWithIdentity: false,
        // Incremented on every boot() and on shutdown so an in-flight boot poll
        // can detect it has been superseded/cancelled and stop.
        bootGeneration: 0
      };
    },
    methods: {
      /**
       * Boot Intercom with user data
       * @param {Object} options - Boot options
       * @param {string} options.app_id - Intercom app ID
       * @param {string} [options.api_base] - Intercom API base URL
       * @param {string} [options.user_id] - User ID
       * @param {string} [options.name] - User name
       * @param {string} [options.email] - User email
       * @param {string} [options.intercom_user_jwt] - Intercom user JWT for identity verification
       * @returns {Promise<void>}
       */
      boot(options) {
        return new Promise((resolve, reject) => {
          if (typeof window === 'undefined') {
            log('Window is undefined, cannot boot');
            reject(new Error('Window is undefined'));
            return;
          }

          if (!INTERCOM_ID) {
            log('Intercom: Disabled (no VUE_APP_INTERCOM_ID configured)');
            reject(new Error('No Intercom ID configured'));
            return;
          }

          if (this.isBooted) {
            if (options.user_id && !this.bootedWithIdentity) {
              log('Upgrading from anonymous to identified session');
              this.shutdownForReboot();
            } else {
              log('Already booted, updating instead');
              const { intercom_user_jwt, app_id, api_base, ...userOptions } = options;
              this.update(userOptions);
              resolve();
              return;
            }
          }

          // Load Intercom script on first boot
          if (!this.isLoaded && !this.isLoading) {
            this.isLoading = true;
            this.loadIntercomScript();
          }

          // If JWT is provided, set it in window.intercomSettings before booting
          if (options.intercom_user_jwt) {
            window.intercomSettings = window.intercomSettings || {};
            window.intercomSettings.intercom_user_jwt = options.intercom_user_jwt;
          }

          // Capture this boot's generation. If shutdown() (e.g. logout) or a newer
          // boot() runs while we are still waiting for the script, the generation
          // changes and this in-flight boot aborts instead of booting a stale user.
          const myGeneration = ++this.bootGeneration;

          // Wait for script to load
          const checkLoaded = setInterval(() => {
            // Aborted by a shutdown or superseded by a newer boot
            if (myGeneration !== this.bootGeneration) {
              clearInterval(checkLoaded);
              clearTimeout(timeoutHandle);
              this.clearIntercomJwt();
              reject(new Error('Intercom boot cancelled'));
              return;
            }

            if (this.isLoaded && window.Intercom) {
              clearInterval(checkLoaded);
              clearTimeout(timeoutHandle);

              // Check if another concurrent boot() call already booted Intercom
              if (this.isBooted) {
                if (options.user_id && !this.bootedWithIdentity) {
                  log('Upgrading concurrent anonymous boot to identified session');
                  this.shutdownForReboot();
                } else {
                  log('Already booted by concurrent call, updating instead');
                  const { intercom_user_jwt, app_id, api_base, ...userOptions } = options;
                  this.update(userOptions);
                  resolve();
                  return;
                }
              }

              // Set isBooted before calling boot() to prevent other intervals from booting
              this.isBooted = true;

              try {
                // Remove JWT from boot options as it's already in intercomSettings
                const { intercom_user_jwt, ...bootOptions } = options;

                const finalBootOptions = {
                  api_base: INTERCOM_API_BASE,
                  ...bootOptions
                };

                window.Intercom('boot', finalBootOptions);
                this.bootedWithIdentity = !!bootOptions.user_id;

                // Force update to ensure user attributes are set (only for identified users)
                if (bootOptions.user_id) {
                  try {
                    window.Intercom('update', {
                      user_id: bootOptions.user_id,
                      name: bootOptions.name,
                      email: bootOptions.email
                    });
                  } catch (updateError) {
                    log('Intercom: Update after boot failed', updateError);
                  }
                }

                // Boot has consumed the JWT; drop it from window.intercomSettings
                // now to minimize exposure to other scripts, matching the cleanup
                // already done on shutdown/failure/timeout/cancel.
                this.clearIntercomJwt();

                resolve();
              } catch (error) {
                this.isBooted = false;
                // Clear JWT on boot failure to prevent credential exposure to other scripts
                this.clearIntercomJwt();
                log('Intercom: Boot failed', error);
                reject(error);
              }
            }
          }, SCRIPT_POLL_INTERVAL_MS);

          // Timeout after 10 seconds
          const timeoutHandle = setTimeout(() => {
            clearInterval(checkLoaded);
            if (!this.isBooted) {
              this.isLoading = false;
              // Clear JWT on timeout to prevent credential exposure to other scripts
              this.clearIntercomJwt();
              const error = new Error('Intercom script failed to load (timeout or network error)');
              log('Intercom: Script load timeout - check network, CSP, or ad blockers', error);
              reject(error);
            }
          }, SCRIPT_LOAD_TIMEOUT_MS);
        });
      },

      /**
       * Update Intercom with new user data
       * @param {Object} [data] - User data to update
       */
      update(data) {
        if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
          try {
            window.Intercom('update', data || {});
          } catch (error) {
            log('Intercom: Update failed', error);
          }
        }
      },

      /**
       * Show the Intercom messenger
       */
      show() {
        if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
          try {
            window.Intercom('show');
          } catch (error) {
            log('Intercom: Show failed', error);
          }
        }
      },

      /**
       * Open the Intercom messenger if it is booted, otherwise open a fallback
       * support URL in a new tab. Shared by every "Get Help" / "Contact us" entry
       * point so they cannot drift on the fallback URL or the booted check.
       *
       * We deliberately do NOT fall back to window.Intercom('show'): the plugin
       * installs a queueing stub before the widget loads, so when Intercom is
       * blocked or times out that stub would silently swallow the click. Opening
       * the support portal instead guarantees the button always does something.
       * @param {string} [fallbackUrl] - URL opened in a new tab when Intercom is unavailable
       */
      openOrFallback(fallbackUrl = SUPPORT_FALLBACK_URL) {
        if (this.isBooted) {
          this.show();
          return;
        }
        if (typeof window !== 'undefined' && fallbackUrl) {
          window.open(fallbackUrl, '_blank', 'noopener');
        }
      },

      /**
       * Hide the Intercom messenger
       */
      hide() {
        if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
          try {
            window.Intercom('hide');
          } catch (error) {
            log('Intercom: Hide failed', error);
          }
        }
      },

      /**
       * Shutdown Intercom - clears user session
       */
      shutdown() {
        // Cancel any in-flight boot so a pending poll cannot boot a stale session
        // (e.g. user logs out while an identified boot is still awaiting the script).
        this.bootGeneration++;
        this.isLoading = false;

        if (typeof window !== 'undefined') {
          // Clear JWT from window.intercomSettings to prevent credential leakage across sessions
          this.clearIntercomJwt();

          if (window.Intercom && this.isBooted) {
            try {
              window.Intercom('shutdown');
            } catch (error) {
              log('Intercom: Shutdown failed', error);
            }
            this.isBooted = false;
            this.bootedWithIdentity = false;
          }
        }
      },

      /**
       * Clear the Intercom JWT from window.intercomSettings to prevent credential
       * exposure. Called on shutdown, boot failure, timeout, and boot cancellation.
       */
      clearIntercomJwt() {
        if (typeof window !== 'undefined' && window.intercomSettings?.intercom_user_jwt) {
          delete window.intercomSettings.intercom_user_jwt;
        }
      },

      /**
       * Internal shutdown for re-booting (anonymous to identified transition).
       * Resets boot state but keeps the script loaded.
       */
      shutdownForReboot() {
        if (typeof window !== 'undefined' && window.Intercom) {
          try {
            window.Intercom('shutdown');
          } catch (error) {
            log('Intercom: Shutdown for reboot failed', error);
          }
        }
        this.isBooted = false;
        this.bootedWithIdentity = false;
      },

      /**
       * Track an event in Intercom
       * @param {string} eventName - Name of the event
       * @param {Object} [metadata] - Event metadata
       */
      trackEvent(eventName, metadata) {
        if (typeof window !== 'undefined' && window.Intercom && this.isBooted) {
          try {
            window.Intercom('trackEvent', eventName, metadata);
          } catch (error) {
            log('Intercom: Track event failed', error);
          }
        }
      },

      /**
       * Check if Intercom is booted
       * @returns {boolean}
       */
      isIntercomBooted() {
        return this.isBooted;
      },

      /**
       * Load the Intercom script dynamically
       */
      loadIntercomScript() {
        if (this.isLoaded || typeof window === 'undefined') return;

        if (!INTERCOM_ID) {
          log('Intercom: Disabled (no VUE_APP_INTERCOM_ID configured), skipping script load');
          return;
        }

        this.initializeIntercomFunction();

        // Set global Intercom settings (without JWT - will be added in boot)
        window.intercomSettings = {
          api_base: INTERCOM_API_BASE,
          app_id: INTERCOM_ID
        };

        // Create and inject script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${INTERCOM_ID}`;

        script.onload = () => {
          this.isLoaded = true;
          this.isLoading = false;
          log('Intercom: Script loaded successfully');
        };

        script.onerror = (error) => {
          this.isLoading = false;
          log('Intercom: Failed to load script', error);
        };

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          const target = document.head || document.body;
          target.appendChild(script);
          log(
            `No existing scripts found, appending to ${target === document.head ? 'document.head' : 'document.body'}`
          );
        }
      },

      /**
       * Initialize the Intercom function on the window object
       */
      initializeIntercomFunction() {
        if (typeof window === 'undefined') return;

        const ic = window.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', window.intercomSettings);
        } else {
          const stub = (...args) => {
            stub.c(args);
          };
          stub.q = [];
          stub.c = (args) => {
            stub.q.push(args);
          };
          window.Intercom = stub;
        }
      }
    }
  });

  return instance;
};

export const IntercomPlugin = {
  install(Vue) {
    Vue.prototype.$intercom = useIntercom();
  }
};
